"use strict";
/**
 * Theme Service
 *
 * Owns the light/dark theme preference. Three states:
 *
 *   "system"  follow the OS (`prefers-color-scheme`) — the default
 *   "light"   explicit override
 *   "dark"    explicit override
 *
 * Applying a theme means stamping `data-theme` on `<html>`; the stylesheet's
 * token mapping does the rest. Nothing here touches the order path — the only
 * work is one attribute write, and only when the user flips the toggle.
 *
 * "system" removes the attribute entirely rather than writing the resolved
 * value, so the pure-CSS `@media (prefers-color-scheme: dark)` rule stays in
 * charge. That is what lets the OS default render correctly at first paint
 * with no JavaScript at all.
 *
 * The same storage key and semantics are duplicated in `public/theme-init.js`,
 * which runs before first paint to avoid a flash. Keep the two in sync — a
 * unit test asserts they agree.
 *
 * @module services/theme-service
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.THEME_STORAGE_KEY = void 0;
exports.getThemePreference = getThemePreference;
exports.resolveTheme = resolveTheme;
exports.getResolvedTheme = getResolvedTheme;
exports.applyTheme = applyTheme;
exports.setThemePreference = setThemePreference;
exports.initTheme = initTheme;
exports.watchSystemTheme = watchSystemTheme;
/** localStorage key holding the user's explicit preference, if any. */
exports.THEME_STORAGE_KEY = "kalshi-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
/**
 * Read the stored preference.
 *
 * Anything unrecognised (or a `localStorage` that throws — Safari private
 * mode) falls back to "system", which is also the safe default: the CSS
 * handles it without us.
 */
function getThemePreference() {
    try {
        const raw = localStorage.getItem(exports.THEME_STORAGE_KEY);
        if (raw === "light" || raw === "dark" || raw === "system")
            return raw;
    }
    catch {
        /* storage unavailable — fall through to the default */
    }
    return "system";
}
/** What the given preference actually renders as right now. */
function resolveTheme(pref) {
    if (pref === "light" || pref === "dark")
        return pref;
    try {
        return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
    }
    catch {
        return "light";
    }
}
/** The theme currently on screen. */
function getResolvedTheme() {
    return resolveTheme(getThemePreference());
}
/**
 * Stamp the preference onto `<html>`.
 *
 * "system" REMOVES the attribute so the media query governs; writing the
 * resolved value instead would freeze the theme and stop it following the OS.
 */
function applyTheme(pref) {
    const root = document.documentElement;
    if (pref === "system") {
        root.removeAttribute("data-theme");
    }
    else {
        root.setAttribute("data-theme", pref);
    }
}
/** Persist and apply. Storage failures must not block the visual change. */
function setThemePreference(pref) {
    try {
        if (pref === "system") {
            localStorage.removeItem(exports.THEME_STORAGE_KEY);
        }
        else {
            localStorage.setItem(exports.THEME_STORAGE_KEY, pref);
        }
    }
    catch {
        /* storage unavailable — still apply for this session */
    }
    applyTheme(pref);
}
/**
 * Re-apply the stored preference. Called once at startup so the client agrees
 * with whatever `theme-init.js` already stamped.
 */
function initTheme() {
    applyTheme(getThemePreference());
}
/**
 * Notify when the OS preference changes, but only while we are following it.
 *
 * @param onChange - Receives the newly resolved theme.
 * @returns An unsubscribe function.
 */
function watchSystemTheme(onChange) {
    let mql;
    try {
        mql = window.matchMedia(DARK_QUERY);
    }
    catch {
        return () => { };
    }
    const handler = () => {
        // An explicit choice wins; ignore the OS until the user goes back to
        // "system".
        if (getThemePreference() !== "system")
            return;
        onChange(mql.matches ? "dark" : "light");
    };
    // addEventListener is not available on MediaQueryList in older WebKit.
    if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
}
